import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, timer } from "rxjs";
import {
  catchError,
  distinctUntilChanged,
  map,
  switchMap,
} from "rxjs/operators";
import { appConfig } from "../../app.config";
import { BackOfficeApiService } from "../shared-backOffice/services/back-office-api.service";

export type BackOfficeHealthStatus = "UP" | "DOWN";

interface BackOfficeHealthResponse {
  status?: string;
}

@Injectable({
  providedIn: "root",
})
export class BackOfficeService {
  private readonly healthRefreshIntervalMs = 30000;

  public backOfficeSidebarSubject = new BehaviorSubject<boolean>(false);

  public backOfficeSidebar$ = this.backOfficeSidebarSubject.asObservable();

  constructor(private backOfficeApiService: BackOfficeApiService) {}

  openSidebar(): void {
    this.backOfficeSidebarSubject.next(true);
  }

  closeSidebar(): void {
    this.backOfficeSidebarSubject.next(false);
  }

  toggleSidebar(): void {
    const current = this.backOfficeSidebarSubject.value;
    this.backOfficeSidebarSubject.next(!current);
  }

  setSidebarState(state: boolean): void {
    this.backOfficeSidebarSubject.next(state);
  }

  watchBackOfficeHealth(): Observable<BackOfficeHealthStatus> {
    return timer(0, this.healthRefreshIntervalMs).pipe(
      switchMap(() => this.getBackOfficeHealth()),
      distinctUntilChanged()
    );
  }

  getBackOfficeHealth(): Observable<BackOfficeHealthStatus> {
    return this.backOfficeApiService
      .get<BackOfficeHealthResponse>(appConfig.backOfficeHealth, undefined, {
        silentError: true,
        showLoading: false,
      })
      .pipe(
        map((response) => this.toBackOfficeHealthStatus(response)),
        catchError(() => of("DOWN" as BackOfficeHealthStatus))
      );
  }

  private toBackOfficeHealthStatus(
    response: BackOfficeHealthResponse
  ): BackOfficeHealthStatus {
    const status = response.status || "";

    return String(status).toUpperCase() === "UP" ? "UP" : "DOWN";
  }
}
